/**
 * CaptainPortal.js - Captain-facing portal accessed via magic link token.
 * Mobile-first, inline-styled. No auth required — token-based access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { verifyCaptainToken, getCaptainSchedule, updateCaptainInfo, submitCaptainScore } from '../services/api';
import { getSportConfig } from '../utils/sportConfig';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';

function CaptainPortal() {
  const { token } = useParams();
  const { colors } = useTheme();
  const styles = getStyles(colors);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [team, setTeam] = useState(null);
  const [league, setLeague] = useState(null);
  const [standings, setStandings] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [scoringGameId, setScoringGameId] = useState(null);
  const [scoreInputs, setScoreInputs] = useState({ team1Score: '', team2Score: '' });
  const [submittingScore, setSubmittingScore] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    async function loadData() {
      try {
        const [verifyData, scheduleData] = await Promise.all([
          verifyCaptainToken(token),
          getCaptainSchedule(token)
        ]);

        setTeam(verifyData.team);
        setLeague(verifyData.league);
        setStandings(verifyData.standings);
        setUpcomingGames(verifyData.upcomingGames);
        setSchedule(scheduleData.schedule);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token]);

  // Set body background on mount and theme change
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = colors.bgSecondary;
    return () => { document.body.style.background = prev; };
  }, [colors.bgSecondary]);

  const handleEditStart = () => {
    setEditName(team.captainName || '');
    setEditEmail(team.captainEmail || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateCaptainInfo(token, {
        captainName: editName,
        captainEmail: editEmail
      });
      setTeam(prev => ({
        ...prev,
        captainName: result.team.captainName,
        captainEmail: result.team.captainEmail
      }));
      setEditing(false);
      showToast('Info updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const sportConfig = league ? getSportConfig(league.sport) : getSportConfig();

  const handleSubmitScore = async (gameId) => {
    const t1 = parseInt(scoreInputs.team1Score, 10);
    const t2 = parseInt(scoreInputs.team2Score, 10);
    if (isNaN(t1) || isNaN(t2) || t1 < 0 || t2 < 0) {
      showToast('Please enter valid scores', 'error');
      return;
    }
    if (!sportConfig.allowTies && t1 === t2) {
      showToast(`Ties are not allowed in ${sportConfig.label}`, 'error');
      return;
    }
    setSubmittingScore(true);
    try {
      await submitCaptainScore(token, gameId, { team1Score: t1, team2Score: t2 });
      // Update local schedule to show pending status
      setSchedule(prev => prev.map(week => ({
        ...week,
        games: week.games.map(g =>
          g.id === gameId ? { ...g, pendingScore: { team1Score: t1, team2Score: t2 } } : g
        )
      })));
      setScoringGameId(null);
      setScoreInputs({ team1Score: '', team2Score: '' });
      showToast('Score submitted — waiting for admin approval');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmittingScore(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // --- Loading ---
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Logo size="2rem" />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <Logo size="2rem" />
        <div style={styles.errorCard}>
          <h2 style={styles.errorTitle}>Link Unavailable</h2>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.errorHint}>This link may have expired. Contact your league organizer for a new one.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'error' ? colors.danger : colors.success
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <Logo size="1.1rem" variant="white" />
        <h1 style={styles.teamName}>{team.name}</h1>
        <p style={styles.leagueName}>{league.name} {league.seasonYear ? `\u2022 ${league.seasonYear}` : ''}</p>
        {league.slug && (
          <a
            href={`/public/${league.slug}`}
            style={{
              display: 'inline-block',
              marginTop: '8px',
              fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.85)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.4)',
              paddingBottom: '1px'
            }}
          >
            View league website →
          </a>
        )}
      </div>

      {/* Upcoming Games */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Upcoming Games</h2>
        {upcomingGames.length === 0 ? (
          <p style={styles.emptyText}>No upcoming games scheduled.</p>
        ) : (
          upcomingGames.slice(0, 5).map(game => {
            const opponent = game.team1 === team.name ? game.team2 : game.team1;
            const isHome = game.team1 === team.name;
            return (
              <div key={game.id} style={styles.upcomingCard}>
                <div style={styles.upcomingDate}>
                  <span style={styles.upcomingDateText}>{formatDate(game.date)}</span>
                  <span style={styles.upcomingTime}>{game.time}</span>
                </div>
                <div style={styles.upcomingDetails}>
                  <span style={styles.upcomingOpponent}>vs {opponent}</span>
                  <span style={styles.upcomingMeta}>Week {game.week} {isHome ? '\u2022 Home' : '\u2022 Away'}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Full Schedule */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={styles.sectionTitle}>Full Schedule</h2>
          <span style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
            {schedule.reduce((acc, w) => acc + w.games.filter(g => g.played).length, 0)} of {schedule.reduce((acc, w) => acc + w.games.length, 0)} played
          </span>
        </div>
        {schedule.map(week => (
          <div key={week.week} style={styles.weekCard}>
            <div style={styles.weekHeader}>
              <span style={styles.weekLabel}>Week {week.week}</span>
              <span style={styles.weekDate}>{formatDate(week.date)}</span>
            </div>
            {week.games.map(game => {
              const isTeam1 = game.team1 === team.name;
              const opponent = isTeam1 ? game.team2 : game.team1;
              const myScore = isTeam1 ? game.team1Score : game.team2Score;
              const theirScore = isTeam1 ? game.team2Score : game.team1Score;
              let resultLabel = null;
              if (game.played) {
                if (myScore > theirScore) resultLabel = { text: 'W', color: colors.success };
                else if (myScore < theirScore) resultLabel = { text: 'L', color: colors.danger };
                else resultLabel = { text: 'T', color: colors.warning };
              }
              const isScoring = scoringGameId === game.id;
              return (
                <div key={game.id}>
                  <div style={styles.scheduleRow}>
                    <div style={styles.scheduleTime}>{game.time}</div>
                    <div style={styles.scheduleOpponent}>vs {opponent}</div>
                    {game.status === 'forfeit' ? (
                      <div style={styles.pendingBadge}>
                        <span style={{ padding: '3px 8px', background: '#fff3e0', color: '#e67e22', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>FORFEIT</span>
                      </div>
                    ) : game.status === 'cancelled' ? (
                      <div style={styles.pendingBadge}>
                        <span style={{ padding: '3px 8px', background: '#fde8e8', color: '#e74c3c', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>CANCELLED</span>
                      </div>
                    ) : game.status === 'postponed' ? (
                      <div style={{ ...styles.pendingBadge, flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ padding: '3px 8px', background: '#fef3cd', color: '#d4a017', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>POSTPONED</span>
                        {game.rescheduledDate && (
                          <span style={{ fontSize: '0.7rem', color: colors.textSecondary, marginTop: 2 }}>{formatDate(game.rescheduledDate)}{game.rescheduledTime ? ` ${game.rescheduledTime}` : ''}</span>
                        )}
                      </div>
                    ) : game.played ? (
                      <div style={styles.scheduleScore}>
                        <span style={{ ...styles.resultBadge, background: resultLabel.color }}>{resultLabel.text}</span>
                        {sportConfig.winLossOnly ? (
                          <span style={styles.scoreText}>
                            {resultLabel.text === 'W' ? 'Win' : resultLabel.text === 'L' ? 'Loss' : 'Draw'}
                          </span>
                        ) : (
                          <span style={styles.scoreText}>{myScore}-{theirScore}</span>
                        )}
                      </div>
                    ) : game.pendingScore ? (
                      <div style={styles.pendingBadge}>
                        <span style={styles.pendingBadgeText}>Pending</span>
                      </div>
                    ) : league.captainScoreEntry && game.status !== 'cancelled' && game.status !== 'forfeit' && game.status !== 'postponed' ? (
                      <button
                        onClick={() => {
                          setScoringGameId(game.id);
                          setScoreInputs({ team1Score: '', team2Score: '' });
                        }}
                        style={styles.submitScoreBtn}
                      >
                        Submit
                      </button>
                    ) : (
                      <div style={styles.schedulePending}>--</div>
                    )}
                  </div>
                  {isScoring && (() => {
                    const t1 = parseInt(scoreInputs.team1Score, 10);
                    const t2 = parseInt(scoreInputs.team2Score, 10);
                    const bothFilled = !isNaN(t1) && !isNaN(t2);
                    const isTied = bothFilled && t1 === t2;
                    const tieBlocked = isTied && !sportConfig.allowTies;

                    // For winLossOnly sports, derive dropdown value from scoreInputs
                    const winLossValue = sportConfig.winLossOnly
                      ? (scoreInputs.team1Score === 1 && scoreInputs.team2Score === 0 ? 'team1'
                        : scoreInputs.team1Score === 0 && scoreInputs.team2Score === 1 ? 'team2'
                        : scoreInputs.team1Score === 0 && scoreInputs.team2Score === 0 && scoreInputs.team1Score !== '' ? 'draw'
                        : '')
                      : '';

                    const handleWinLossChange = (val) => {
                      if (val === 'team1') setScoreInputs({ team1Score: 1, team2Score: 0 });
                      else if (val === 'team2') setScoreInputs({ team1Score: 0, team2Score: 1 });
                      else if (val === 'draw') setScoreInputs({ team1Score: 0, team2Score: 0 });
                      else setScoreInputs({ team1Score: '', team2Score: '' });
                    };

                    return (
                    <div style={styles.scoreForm}>
                      {sportConfig.winLossOnly ? (
                        <div style={{ marginBottom: 10, textAlign: 'center' }}>
                          <select
                            value={winLossValue}
                            onChange={e => handleWinLossChange(e.target.value)}
                            style={{
                              ...styles.scoreFormInput,
                              width: '100%',
                              maxWidth: 260,
                              cursor: 'pointer'
                            }}
                          >
                            <option value="">Select result...</option>
                            <option value="team1">{game.team1} wins</option>
                            <option value="team2">{game.team2} wins</option>
                            {sportConfig.allowTies && <option value="draw">Draw</option>}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div style={styles.scoreFormInputs}>
                            <div style={styles.scoreFormGroup}>
                              <label style={styles.scoreFormLabel}>{game.team1} ({sportConfig.scoreLabel})</label>
                              <input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                value={scoreInputs.team1Score}
                                onChange={e => setScoreInputs(prev => ({ ...prev, team1Score: e.target.value }))}
                                style={{ ...styles.scoreFormInput, ...(tieBlocked ? { borderColor: colors.danger } : {}) }}
                                placeholder="0"
                              />
                            </div>
                            <span style={styles.scoreFormDash}>-</span>
                            <div style={styles.scoreFormGroup}>
                              <label style={styles.scoreFormLabel}>{game.team2} ({sportConfig.scoreLabel})</label>
                              <input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                value={scoreInputs.team2Score}
                                onChange={e => setScoreInputs(prev => ({ ...prev, team2Score: e.target.value }))}
                                style={{ ...styles.scoreFormInput, ...(tieBlocked ? { borderColor: colors.danger } : {}) }}
                                placeholder="0"
                              />
                            </div>
                          </div>
                          {tieBlocked && (
                            <div style={{ color: colors.danger, fontSize: '0.8rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                              Ties are not allowed in {sportConfig.label}
                            </div>
                          )}
                        </>
                      )}
                      <div style={styles.scoreFormActions}>
                        <button
                          onClick={() => handleSubmitScore(game.id)}
                          disabled={submittingScore || tieBlocked || (sportConfig.winLossOnly && !winLossValue)}
                          style={{ ...styles.scoreFormSubmit, ...((tieBlocked || (sportConfig.winLossOnly && !winLossValue)) ? { opacity: 0.5 } : {}) }}
                        >
                          {submittingScore ? 'Submitting...' : 'Submit Score'}
                        </button>
                        <button
                          onClick={() => setScoringGameId(null)}
                          style={styles.scoreFormCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Standings */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Standings</h2>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={styles.th}>#</th>
                <th style={{ ...styles.th, textAlign: 'left' }}>Team</th>
                <th style={styles.th}>GP</th>
                <th style={styles.th}>W</th>
                <th style={styles.th}>L</th>
                {sportConfig.allowTies && <th style={styles.th}>T</th>}
                <th style={styles.th}>PTS</th>
                <th style={styles.th}>{sportConfig.statLabels.goalsFor}</th>
                <th style={styles.th}>{sportConfig.statLabels.goalsAgainst}</th>
                <th style={styles.th}>{sportConfig.statLabels.goalDifferential}</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => {
                const isMyTeam = s.id === team.id;
                return (
                  <tr key={s.id} style={isMyTeam ? styles.myTeamRow : (i % 2 === 0 ? styles.evenRow : {})}>
                    <td style={styles.td}>{i + 1}</td>
                    <td style={{ ...styles.td, textAlign: 'left', fontWeight: isMyTeam ? 600 : 400 }}>
                      {s.name}
                    </td>
                    <td style={styles.td}>{s.gamesPlayed}</td>
                    <td style={styles.td}>{s.wins}</td>
                    <td style={styles.td}>{s.losses}</td>
                    {sportConfig.allowTies && <td style={styles.td}>{s.ties}</td>}
                    <td style={{ ...styles.td, fontWeight: 600 }}>{s.points}</td>
                    <td style={styles.td}>{s.goalsFor}</td>
                    <td style={styles.td}>{s.goalsAgainst}</td>
                    <td style={styles.td}>{(s.goalsFor - s.goalsAgainst) > 0 ? '+' : ''}{s.goalsFor - s.goalsAgainst}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Captain Info */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Captain Info</h2>
        {!editing ? (
          <div style={styles.infoCard}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Name</span>
              <span style={styles.infoValue}>{team.captainName || 'Not set'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Email</span>
              <span style={styles.infoValue}>{team.captainEmail || 'Not set'}</span>
            </div>
            <button onClick={handleEditStart} style={styles.editButton}>
              Update Info
            </button>
          </div>
        ) : (
          <div style={styles.infoCard}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Name</label>
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                style={styles.formInput}
                placeholder="Captain name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Email</label>
              <input
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                style={styles.formInput}
                placeholder="captain@email.com"
              />
            </div>
            <div style={styles.formActions}>
              <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <Logo size="0.9rem" />
      </div>
    </div>
  );
}

const getStyles = (colors) => ({
  page: {
    maxWidth: 600,
    margin: '0 auto',
    paddingBottom: 40,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: colors.text
  },

  // Loading & Error
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loadingText: { color: colors.textSecondary, marginTop: 12 },
  errorCard: {
    marginTop: 24,
    padding: '24px 20px',
    background: colors.card,
    borderRadius: 12,
    textAlign: 'center',
    maxWidth: 360,
    boxShadow: `0 2px 8px ${colors.shadow}`
  },
  errorTitle: { margin: 0, fontSize: '1.2rem', color: colors.text },
  errorText: { color: colors.danger, margin: '12px 0 0' },
  errorHint: { color: colors.textSecondary, fontSize: '0.85rem', margin: '8px 0 0' },

  // Header (gradient background — keep #fff for text)
  header: {
    background: colors.brandGradient,
    padding: '24px 20px 20px',
    color: '#fff',
    textAlign: 'center'
  },
  teamName: {
    margin: '10px 0 4px',
    fontSize: '1.5rem',
    fontWeight: 700
  },
  leagueName: {
    margin: 0,
    fontSize: '0.9rem',
    opacity: 0.9
  },

  // Toast (colored backgrounds — keep #fff for text)
  toast: {
    position: 'fixed',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: '0.85rem',
    fontWeight: 500,
    zIndex: 1000,
    boxShadow: `0 4px 12px ${colors.shadowHover}`
  },

  // Sections
  section: { padding: '0 16px', marginTop: 24 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.text,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: `2px solid ${colors.brand}`
  },
  emptyText: { color: colors.textSecondary, fontSize: '0.9rem' },

  // Upcoming Games
  upcomingCard: {
    display: 'flex',
    alignItems: 'center',
    background: colors.card,
    borderRadius: 10,
    padding: '12px 14px',
    marginBottom: 8,
    boxShadow: `0 1px 4px ${colors.shadow}`,
    borderLeft: `4px solid ${colors.brand}`
  },
  upcomingDate: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 90,
    marginRight: 14
  },
  upcomingDateText: { fontSize: '0.85rem', fontWeight: 600, color: colors.text },
  upcomingTime: { fontSize: '0.78rem', color: colors.textSecondary },
  upcomingDetails: { display: 'flex', flexDirection: 'column' },
  upcomingOpponent: { fontSize: '0.95rem', fontWeight: 500, color: colors.text },
  upcomingMeta: { fontSize: '0.78rem', color: colors.textSecondary },

  // Full Schedule
  weekCard: {
    background: colors.card,
    borderRadius: 10,
    marginBottom: 10,
    overflow: 'hidden',
    boxShadow: `0 1px 4px ${colors.shadow}`
  },
  weekHeader: {
    background: colors.brandGradient,
    padding: '8px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  weekLabel: { color: '#fff', fontWeight: 600, fontSize: '0.85rem' },
  weekDate: { color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' },
  scheduleRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    borderBottom: `1px solid ${colors.border}`
  },
  scheduleTime: { width: 70, fontSize: '0.82rem', color: colors.textSecondary, flexShrink: 0 },
  scheduleOpponent: { flex: 1, fontSize: '0.9rem', color: colors.text },
  scheduleScore: { display: 'flex', alignItems: 'center', gap: 6 },
  resultBadge: {
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  scoreText: { fontSize: '0.85rem', fontWeight: 600, color: colors.text },
  schedulePending: { color: colors.textSecondary, fontSize: '0.85rem' },
  submitScoreBtn: {
    padding: '6px 14px',
    background: colors.brandGradient,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
    minHeight: 44
  },
  pendingBadge: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  pendingBadgeText: {
    padding: '3px 8px',
    background: '#fff3e0',
    color: '#e67e22',
    borderRadius: 6,
    fontSize: '0.75rem',
    fontWeight: 600
  },
  scoreForm: {
    padding: '10px 14px 14px',
    background: colors.sectionBg,
    borderBottom: `1px solid ${colors.border}`
  },
  scoreFormInputs: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 10
  },
  scoreFormGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    maxWidth: 120
  },
  scoreFormLabel: {
    fontSize: '0.72rem',
    color: colors.textSecondary,
    marginBottom: 3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    textAlign: 'center'
  },
  scoreFormInput: {
    width: '100%',
    padding: '10px',
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 6,
    fontSize: '1.1rem',
    textAlign: 'center',
    boxSizing: 'border-box',
    outline: 'none',
    background: colors.inputBg,
    color: colors.text,
    minHeight: 48
  },
  scoreFormDash: {
    fontSize: '1.2rem',
    color: colors.textSecondary,
    fontWeight: 600,
    paddingBottom: 8
  },
  scoreFormActions: {
    display: 'flex',
    gap: 8
  },
  scoreFormSubmit: {
    flex: 1,
    padding: '10px',
    background: colors.brandGradient,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 44
  },
  scoreFormCancel: {
    flex: 1,
    padding: '10px',
    background: colors.bgSecondary,
    color: colors.textSecondary,
    border: 'none',
    borderRadius: 6,
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: 44
  },

  // Standings Table
  tableWrapper: { overflowX: 'auto', borderRadius: 10, boxShadow: `0 1px 4px ${colors.shadow}` },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: colors.tableBg,
    fontSize: '0.85rem'
  },
  tableHeaderRow: {
    background: colors.brandGradient
  },
  th: {
    padding: '8px 6px',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.78rem',
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  td: {
    padding: '8px 6px',
    textAlign: 'center',
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text
  },
  myTeamRow: { background: colors.badgeBg },
  evenRow: { background: colors.tableStripe },

  // Captain Info
  infoCard: {
    background: colors.card,
    borderRadius: 10,
    padding: 16,
    boxShadow: `0 1px 4px ${colors.shadow}`
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: `1px solid ${colors.border}`
  },
  infoLabel: { color: colors.textSecondary, fontSize: '0.85rem' },
  infoValue: { color: colors.text, fontSize: '0.85rem', fontWeight: 500 },
  editButton: {
    marginTop: 12,
    width: '100%',
    padding: '10px',
    background: 'transparent',
    border: `2px solid ${colors.brand}`,
    color: colors.brand,
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  formGroup: { marginBottom: 12 },
  formLabel: { display: 'block', fontSize: '0.82rem', color: colors.textSecondary, marginBottom: 4 },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 8,
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    outline: 'none',
    background: colors.inputBg,
    color: colors.text
  },
  formActions: { display: 'flex', gap: 8, marginTop: 4 },
  saveButton: {
    flex: 1,
    padding: '10px',
    background: colors.brandGradient,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    background: colors.bgSecondary,
    color: colors.textSecondary,
    border: 'none',
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer'
  },

  // Footer
  footer: {
    textAlign: 'center',
    padding: '32px 16px 16px',
    opacity: 0.6
  }
});

export default CaptainPortal;
